import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

import * as AuthAPI from '@/apis/auth';
import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useDatadogStore } from '@/stores/datadog';
import { useIdentityStore } from '@/stores/identity';
import { useImpersonatorStore } from '@/stores/impersonator';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';
import { loadScript } from '@/utils/dom';
import { isE2E } from '@/utils/e2e';
import { logger } from '@/utils/logger';

type WidgetEventPayload = { conversation?: { conversationId?: string | number } };
type HubSpotWidget = {
  status: () => { loaded: boolean };
  load: () => void;
  refresh: () => void;
  remove: () => void;
  open: () => void;
  close: () => void;
};

declare global {
  interface Window {
    _hsq?: unknown[];
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      inlineEmbedSelector?: string;
      identificationEmail?: string;
      identificationToken?: string;
    };
    hsConversationsOnReady?: (() => void)[];
    hsCallsToActionsReady?: (() => void)[];
    HubSpotConversations?: {
      widget: HubSpotWidget;
      on: (event: string, handler: (payload?: WidgetEventPayload) => void) => void;
      clear: (options?: { resetWidget?: boolean }) => void;
    };
    HubSpotCallsToActions?: { refresh: () => void };
  }
}

// HubSpot visitor tokens expire after 12h; refresh well inside that.
const TOKEN_MAX_AGE_MS = 60 * 60 * 1000;
export const LAST_IDENTIFIED_EMAIL_KEY = 'hubspotLastIdentifiedEmail';
// Recorded to RUM so a "chat disappeared" report can be traced to the widget call that preceded it.
const TRACKED_WIDGET_EVENTS = [
  'conversationStarted',
  'conversationClosed',
  'userSelectedThread',
  'widgetLoaded',
  'widgetClosed',
];

async function getToken() {
  try {
    const res = await AuthAPI.getHubspotSupportToken();
    return res.data.token;
  } catch {
    return '';
  }
}

// A full page load under another user (login switch) would otherwise inherit the previous
// user's messagesUtk cookie, and HubSpot moves that visitor's open thread to this browser.
function isDifferentVisitorThanLastPageLoad(email: string) {
  const previous = browserStorageGetItem(LAST_IDENTIFIED_EMAIL_KEY);
  return !!previous && previous !== email;
}

function trackChatAction(name: string, context: Record<string, unknown> = {}) {
  useDatadogStore().addAction(`hubspot_chat.${name}`, {
    ...context,
    path: window.location.pathname,
  });
}

function trackWidgetEvent(name: string, payload?: WidgetEventPayload) {
  trackChatAction(name, { conversationId: payload?.conversation?.conversationId ?? null });
}

function trackEvent({ eventName, eventDetails }: { eventName: string; eventDetails?: unknown }) {
  if (!window._hsq) return;
  window._hsq.push([eventName], eventDetails);
}

export const useHubspotStore = defineStore('hubspot', () => {
  const identityStore = useIdentityStore();
  const impersonatorStore = useImpersonatorStore();
  const ready = ref(false);
  const ctaReady = ref(false);
  const isOpen = ref(false);
  const loadingWidget = ref(false);
  const showConversation = ref(false);
  const lastIdentifiedEmail = ref<string | null>(null);
  let initGeneration = 0;
  let booted = false;
  let tokenFetchedAt = 0;

  const enabled = env.hubspotEnabled && !isDatadogSyntheticsBrowser && !isE2E();

  function onWidgetLoaded() {
    loadingWidget.value = false;
  }

  function onWidgetClosed() {
    isOpen.value = false;
  }

  function onConversationsAPIReady() {
    ready.value = true;
    // Register listeners once. Re-binding on every initWidget() would accumulate
    // handlers across impersonation switches.
    const conversations = window.HubSpotConversations;
    if (conversations) {
      conversations.on('widgetLoaded', onWidgetLoaded);
      conversations.on('widgetClosed', onWidgetClosed);
      TRACKED_WIDGET_EVENTS.forEach((name) => {
        conversations.on(name, (payload) => trackWidgetEvent(name, payload));
      });
    }
  }

  function onCallToActionAPIReady() {
    ctaReady.value = true;
  }

  function boot() {
    // Always init the tracking queue. trackEvent() runs from router.afterEach
    // hooks even when HubSpot itself is disabled (e2e, synthetics), so
    // _hsq must always be defined. Idempotent — the hs-scripts.com loader
    // replaces _hsq with its own tracker object once ready.
    window._hsq = window._hsq || [];

    // Runs on every brand change, including route-driven ones. Re-running would wipe
    // the identification token and re-inject the loader, so HubSpot sees a new visitor.
    if (enabled && !booted) {
      booted = true;
      window.hsConversationsSettings = {
        loadImmediately: false,
        inlineEmbedSelector: '#dh-hubspot-chat',
      };

      if (window.HubSpotConversations) {
        onConversationsAPIReady();
      } else {
        window.hsConversationsOnReady = [onConversationsAPIReady];
      }

      if (window.HubSpotCallsToActions) {
        onCallToActionAPIReady();
      } else {
        window.hsCallsToActionsReady = [onCallToActionAPIReady];
      }

      loadScript(`//js-na1.hs-scripts.com/${env.hubspotTrackingCode}.js`, {
        id: 'hs-script-loader',
      });
    }
  }

  async function loadWidget() {
    const widget = window.HubSpotConversations!.widget;
    if (
      !widget.status().loaded &&
      identityStore.identity &&
      !window.hsConversationsSettings?.identificationToken
    ) {
      // An unidentified load drops a messagesUtk cookie and starts a fresh conversation.
      // A superseded init leaves loading to the newer one.
      const pending = initWidget();
      const gen = initGeneration;
      await pending;
      if (gen !== initGeneration) return;
    }
    // No refresh() on a loaded widget: re-rendering on every open/close is the suspected cause
    // of visitors landing in a new thread while their previous one was still open.
    if (!widget.status().loaded && !loadingWidget.value) {
      widget.load();
      loadingWidget.value = true;
      trackChatAction('load', {
        identified: !!window.hsConversationsSettings?.identificationToken,
        cleared: false,
      });
    }
  }

  // Re-fetches the visitor token when older than an hour so whatever next loads the
  // widget never hands HubSpot an expired (12h) token. Leaves the widget alone.
  async function refreshTokenIfStale() {
    const email = identityStore.identity?.email;
    if (!enabled || !email || !tokenFetchedAt || Date.now() - tokenFetchedAt < TOKEN_MAX_AGE_MS) {
      return;
    }
    const gen = initGeneration;
    const token = await getToken();
    if (!token || gen !== initGeneration || identityStore.identity?.email !== email) return;
    window.hsConversationsSettings!.identificationToken = token;
    tokenFetchedAt = Date.now();
  }

  function teardown() {
    initGeneration += 1;
    if (window.hsConversationsSettings) {
      delete window.hsConversationsSettings.identificationEmail;
      delete window.hsConversationsSettings.identificationToken;
    }
    const widget = window.HubSpotConversations?.widget;
    if (widget?.status?.().loaded) {
      window.HubSpotConversations!.clear({ resetWidget: true });
      widget.remove();
      trackChatAction('teardown');
    }
    loadingWidget.value = false;
    isOpen.value = false;
    showConversation.value = false;
    lastIdentifiedEmail.value = null;
    tokenFetchedAt = 0;
  }

  async function initWidget() {
    const identity = identityStore.identity;
    if (!identity) {
      teardown();
      return;
    }

    const email = identity.email;
    const gen = ++initGeneration;

    const token = await getToken();
    if (gen !== initGeneration) return;
    if (!token) {
      // Don't tear down a working session over a transient token failure. The
      // tradeoff: agent will see "anonymous visitor" if this fails on first init.
      logger.warn('[hubspot] visitor token unavailable; skipping widget identity update');
      return;
    }

    window.hsConversationsSettings!.identificationEmail = email;
    window.hsConversationsSettings!.identificationToken = token;
    tokenFetchedAt = Date.now();
    window._hsq?.push(['identify', { email }]);

    const widget = window.HubSpotConversations!.widget;
    const status = widget.status();

    // Same visitor as last time → just re-render. Avoids wiping welcome state and
    // unread badges for returning customers.
    if (status.loaded && lastIdentifiedEmail.value === email) {
      widget.refresh();
      trackChatAction('refresh');
      return;
    }

    // Real identity change (impersonation start/end) or first load: clear the old
    // visitor's messagesUtk cookie and recreate the widget instance under the new
    // identification token. widget.refresh() does NOT re-identify; widget.load() does.
    let cleared = false;
    if (status.loaded) {
      window.HubSpotConversations!.clear({ resetWidget: true });
      cleared = true;
    } else if (isDifferentVisitorThanLastPageLoad(email)) {
      window.HubSpotConversations!.clear();
      cleared = true;
    }
    loadingWidget.value = true;
    widget.load();
    trackChatAction('load', { identified: true, cleared });
    lastIdentifiedEmail.value = email;
    browserStorageSetItem(LAST_IDENTIFIED_EMAIL_KEY, email);
  }

  async function show() {
    if (impersonatorStore.isImpersonating) return;
    await loadWidget();
    window.HubSpotConversations!.widget.open();
    isOpen.value = true;
    showConversation.value = true;
  }

  async function syncWidget(newOpen: boolean) {
    if (impersonatorStore.isImpersonating) return;
    await loadWidget();
    if (newOpen) {
      window.HubSpotConversations!.widget.open();
      isOpen.value = true;
    } else {
      window.HubSpotConversations!.widget.close();
    }
  }

  function refreshCallToAction() {
    if (ctaReady.value) {
      window.HubSpotCallsToActions?.refresh();
    }
  }

  watch(
    () => ctaReady.value,
    () => {
      refreshCallToAction();
    },
    { immediate: true },
  );

  const chatTargetReady = ref(false);

  function chatMounted() {
    chatTargetReady.value = true;
  }

  function chatUnmounted() {
    chatTargetReady.value = false;
  }

  watch(
    () => ({
      identity: identityStore.identity,
      ready: ready.value,
      mounted: chatTargetReady.value,
      impersonating: impersonatorStore.isImpersonating,
    }),
    (context) => {
      if (!enabled || !context.ready) return;
      // Staff identified as the customer would share one chat cookie across every customer
      // they impersonate, pulling each customer's live thread onto the staff browser.
      if (context.impersonating) {
        teardown();
      } else if (context.mounted) {
        if (context.identity) {
          void initWidget();
        } else {
          teardown();
        }
      }
    },
    { immediate: true },
  );

  return {
    loadingWidget,
    enabled,
    ready,
    showConversation,
    boot,
    show,
    syncWidget,
    refreshCallToAction,
    trackEvent,
    refreshTokenIfStale,
    chatMounted,
    chatUnmounted,
    teardown,
  };
});
